import { apiClient } from './apiClient';
import { authService } from './authService';
import {
  StreamEvent,
  ChatSessionListResponse,
  CreateSessionResponse,
  GetMessagesResponse,
} from '../types/chat';

/**
 * Chat API Service
 * Centralizes all communication with the chat-service microservice.
 *
 * - sendMessage: Uses fetch + ReadableStream for SSE streaming (POST /message)
 * - getSessions, createNewSession, getSessionMessages: Use apiClient (axios) for REST
 *
 * Requirements: 8.1, 8.4, 10.1, 10.2, 10.3
 */

const CHAT_API_BASE =
  import.meta.env.VITE_CHAT_API_BASE_URL || 'http://localhost:8085/api/v1/chat';

// ─── Error Code to User-Friendly Message Mapping ─────────────────────────────

type ChatErrorCode =
  | 'AI_UNAVAILABLE'
  | 'DATA_UNAVAILABLE'
  | 'AI_TIMEOUT'
  | 'STREAM_INTERRUPTED'
  | 'RATE_LIMIT'
  | 'ACCESS_DENIED'
  | 'SESSION_EXPIRED';

const ERROR_MESSAGES: Record<ChatErrorCode, string> = {
  AI_UNAVAILABLE: 'AI responses are temporarily unavailable. Please try again later.',
  DATA_UNAVAILABLE: 'Cost data could not be retrieved. Try a different time range or scope.',
  AI_TIMEOUT: 'The response timed out. Please try a simpler question.',
  STREAM_INTERRUPTED: 'Response was interrupted. The partial response is shown above.',
  RATE_LIMIT: 'Too many requests. Please wait a moment before asking another question.',
  ACCESS_DENIED: "You don't have permission to access this data.",
  SESSION_EXPIRED: 'Your chat session has expired. Starting a new conversation.',
};

/**
 * Map an error code from the backend to a user-friendly message.
 */
function mapErrorCodeToMessage(code: string): string {
  return ERROR_MESSAGES[code as ChatErrorCode] || 'An unexpected error occurred. Please try again.';
}

/**
 * Map HTTP status codes to user-friendly error messages.
 */
function mapHttpStatusToMessage(status: number): string {
  switch (status) {
    case 403:
      return ERROR_MESSAGES.ACCESS_DENIED;
    case 410:
      return ERROR_MESSAGES.SESSION_EXPIRED;
    case 429:
      return ERROR_MESSAGES.RATE_LIMIT;
    case 503:
    case 502:
    case 504:
      return ERROR_MESSAGES.AI_UNAVAILABLE;
    default:
      if (status >= 500) {
        return ERROR_MESSAGES.DATA_UNAVAILABLE;
      }
      return 'An unexpected error occurred. Please try again.';
  }
}

// ─── SSE Parsing ─────────────────────────────────────────────────────────────

/**
 * Parse an SSE line into a StreamEvent.
 * SSE format: "event: <type>\ndata: <json>"
 */
function parseSSEEvent(eventType: string, data: string): StreamEvent | null {
  try {
    const parsed = JSON.parse(data);
    switch (eventType) {
      case 'token':
        return { type: 'token', content: parsed.content };
      case 'metadata':
        return {
          type: 'metadata',
          intent: parsed.intent,
          endpointsQueried: parsed.endpointsQueried,
          confidenceScore: parsed.confidenceScore,
          timeRange: parsed.timeRange,
        };
      case 'done':
        return { type: 'done', sessionId: parsed.sessionId, messageId: parsed.messageId };
      case 'error':
        return { type: 'error', code: parsed.code, message: parsed.message };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

// ─── SSE Streaming: sendMessage ──────────────────────────────────────────────

export interface SendMessageCallbacks {
  onToken: (content: string) => void;
  onMetadata: (metadata: {
    intent: string;
    endpointsQueried: string[];
    confidenceScore: number;
    timeRange: { start: string; end: string };
  }) => void;
  onDone: (result: { sessionId: string; messageId: string }) => void;
  onError: (error: { code: string; message: string }) => void;
}

export interface SendMessageParams {
  sessionId?: string;
  message: string;
}

/**
 * Send a chat message and stream the response via SSE.
 * Uses the native fetch API for ReadableStream support.
 *
 * Returns an AbortController so callers can cancel the stream.
 *
 * Requirement 8.1: SSE streaming token reception
 * Requirement 8.4: Stream interruption handling
 * Requirement 10.1-10.3: Error handling with user-friendly messages
 */
async function sendMessage(
  params: SendMessageParams,
  callbacks: SendMessageCallbacks
): Promise<AbortController> {
  const abortController = new AbortController();
  const token = authService.getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${CHAT_API_BASE}/message`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        sessionId: params.sessionId || undefined,
        message: params.message,
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      const userMessage = mapHttpStatusToMessage(response.status);
      callbacks.onError({
        code: response.status.toString(),
        message: userMessage,
      });
      return abortController;
    }

    if (!response.body) {
      callbacks.onError({
        code: 'STREAM_INTERRUPTED',
        message: ERROR_MESSAGES.STREAM_INTERRUPTED,
      });
      return abortController;
    }

    // Process the SSE stream asynchronously
    processStream(response.body, callbacks, abortController.signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Stream was intentionally cancelled by the caller — not an error
      return abortController;
    }

    // Network or fetch error
    callbacks.onError({
      code: 'AI_UNAVAILABLE',
      message: ERROR_MESSAGES.AI_UNAVAILABLE,
    });
  }

  return abortController;
}

/**
 * Process the ReadableStream from an SSE response.
 * Parses SSE frames and dispatches to the appropriate callback.
 */
async function processStream(
  body: ReadableStream<Uint8Array>,
  callbacks: SendMessageCallbacks,
  signal: AbortSignal
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = '';

  try {
    while (true) {
      if (signal.aborted) break;

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (trimmedLine === '') {
          // Empty line = end of event block
          currentEventType = '';
          continue;
        }

        if (trimmedLine.startsWith('event:')) {
          currentEventType = trimmedLine.slice(6).trim();
          continue;
        }

        if (trimmedLine.startsWith('data:')) {
          const data = trimmedLine.slice(5).trim();
          if (!currentEventType || !data) continue;

          const event = parseSSEEvent(currentEventType, data);
          if (!event) continue;

          switch (event.type) {
            case 'token':
              callbacks.onToken(event.content);
              break;
            case 'metadata':
              callbacks.onMetadata({
                intent: event.intent,
                endpointsQueried: event.endpointsQueried,
                confidenceScore: event.confidenceScore,
                timeRange: event.timeRange,
              });
              break;
            case 'done':
              callbacks.onDone({
                sessionId: event.sessionId,
                messageId: event.messageId,
              });
              break;
            case 'error':
              callbacks.onError({
                code: event.code,
                message: mapErrorCodeToMessage(event.code),
              });
              break;
          }
        }
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Stream aborted intentionally
      return;
    }

    // Stream interrupted unexpectedly
    callbacks.onError({
      code: 'STREAM_INTERRUPTED',
      message: ERROR_MESSAGES.STREAM_INTERRUPTED,
    });
  } finally {
    reader.releaseLock();
  }
}

// ─── REST Endpoints (using apiClient with auth interceptors) ─────────────────

/**
 * Get all active chat sessions for the current user.
 * GET /api/v1/chat/sessions
 */
async function getSessions(): Promise<ChatSessionListResponse> {
  const response = await apiClient.get<ChatSessionListResponse>(
    `${CHAT_API_BASE}/sessions`
  );
  return response.data;
}

/**
 * Create a new chat session.
 * POST /api/v1/chat/sessions/new
 */
async function createNewSession(): Promise<CreateSessionResponse> {
  const response = await apiClient.post<CreateSessionResponse>(
    `${CHAT_API_BASE}/sessions/new`
  );
  return response.data;
}

/**
 * Get messages for a specific session.
 * GET /api/v1/chat/sessions/:id/messages
 */
async function getSessionMessages(sessionId: string): Promise<GetMessagesResponse> {
  const response = await apiClient.get<GetMessagesResponse>(
    `${CHAT_API_BASE}/sessions/${sessionId}/messages`
  );
  return response.data;
}

// ─── Exported Chat Service ───────────────────────────────────────────────────

export const chatService = {
  sendMessage,
  getSessions,
  createNewSession,
  getSessionMessages,
};
